import React from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react';
import { Spinner } from './Spinner';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: keyof T;
  header: string;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string | number;
}

interface RowAction<T> {
  label: string;
  onClick: (row: T) => void;
  variant?: 'primary' | 'secondary' | 'danger';
  // WI #337: some rows shouldn't offer some actions — a user who is already
  // inactive has nothing to deactivate. Return true to omit this entry from
  // that row's menu. Omitted actions are simply not rendered.
  hidden?: (row: T) => boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  isLoading?: boolean;
  totalItems?: number;
  currentPage?: number;
  /**
   * Rows per page. Defaults to 20 to match `PaginationParams.page_size` in
   * `backend/app/schemas/common.py` — WI #337: this used to default to 10,
   * so a caller that passed `totalItems` but not `pageSize` rendered
   * "Page 1 of 3" over a list the server had already split into 2.
   */
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  rowKey: keyof T;
  emptyMessage?: string;
  actions?: RowAction<T>[];
  // Bug #166: optional row-click handler so list pages can navigate to a
  // detail view by clicking anywhere in the row. When provided, the row
  // renders with a pointer cursor. The actions-menu trigger stops propagation
  // so opening the menu doesn't also fire the row click.
  onRowClick?: (row: T) => void;
}

export const DataTable = React.forwardRef<
  HTMLDivElement,
  DataTableProps<any>
>(
  (
    {
      columns,
      data,
      isLoading = false,
      totalItems = 0,
      currentPage = 1,
      pageSize = 20,
      onPageChange,
      onSort,
      sortBy,
      sortOrder,
      rowKey,
      emptyMessage = 'No data available',
      actions,
      onRowClick,
    },
    ref
  ) => {
    const [openMenuRowId, setOpenMenuRowId] = React.useState<any>(null);
    // Anchor coordinates (viewport-relative) for the currently-open menu.
    // We render the menu with position: fixed so it escapes the container's
    // overflow clipping (Bug #142 — on the last table row, the menu used to
    // be hidden beneath the table border because `position: absolute` was
    // anchored inside a cell whose wrapper had `overflow: hidden`).
    const [menuPos, setMenuPos] = React.useState<{
      top: number;
      left: number;
      placement: 'below' | 'above';
    } | null>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);
    const triggerRefs = React.useRef<Map<any, HTMLButtonElement>>(new Map());

    React.useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (menuRef.current && !menuRef.current.contains(target)) {
          // Don't close if the click was on the trigger — that would toggle
          // twice and the menu would just re-open.
          const trigger = openMenuRowId != null
            ? triggerRefs.current.get(openMenuRowId)
            : null;
          if (trigger && trigger.contains(target)) return;
          setOpenMenuRowId(null);
          setMenuPos(null);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuRowId]);

    // Close the menu on scroll / resize so it doesn't "float" detached from
    // its row once the user moves the page.
    React.useEffect(() => {
      if (openMenuRowId == null) return;
      const close = () => {
        setOpenMenuRowId(null);
        setMenuPos(null);
      };
      window.addEventListener('scroll', close, true);
      window.addEventListener('resize', close);
      return () => {
        window.removeEventListener('scroll', close, true);
        window.removeEventListener('resize', close);
      };
    }, [openMenuRowId]);

    const openMenuForRow = (rowId: any, trigger: HTMLButtonElement | null) => {
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const menuWidth = 160;
      const estimatedMenuHeight = 40 * (actions?.length ?? 1) + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const placement: 'below' | 'above' =
        spaceBelow < estimatedMenuHeight && rect.top > estimatedMenuHeight
          ? 'above'
          : 'below';
      const top = placement === 'below' ? rect.bottom + 4 : rect.top - estimatedMenuHeight - 4;
      // Right-align the menu with the trigger so it doesn't overflow the
      // viewport on the right edge. Clamp to >= 8px so it never clips left.
      const left = Math.max(8, rect.right - menuWidth);
      setMenuPos({ top, left, placement });
      setOpenMenuRowId(rowId);
    };

    const containerStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'var(--color-white)',
      borderRadius: '8px',
      border: '1px solid var(--color-light-grey)',
      overflow: 'hidden',
    };

    const tableWrapperStyle: React.CSSProperties = {
      overflowX: 'auto',
      flex: 1,
    };

    const tableStyle: React.CSSProperties = {
      width: '100%',
      borderCollapse: 'collapse',
      fontFamily: 'var(--font-family)',
    };

    const theadStyle: React.CSSProperties = {
      backgroundColor: 'var(--color-light-grey)',
      borderBottom: '1px solid var(--color-border)',
    };

    const thStyle: React.CSSProperties = {
      padding: '12px',
      textAlign: 'left',
      fontSize: '12px',
      fontWeight: 600,
      color: 'var(--color-dark-grey)',
      textTransform: 'uppercase',
      userSelect: 'none',
    };

    // Bug #179: removed `display: flex` from the <th> itself — that broke
    // table-cell layout and collapsed all headers into one column. Instead
    // the inner content wrapper uses `display: inline-flex`.
    const sortableTh: React.CSSProperties = {
      ...thStyle,
      cursor: 'pointer',
      transition: 'background-color 0.2s ease-in-out',
    };

    const sortableThContent: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    };

    const tbodyStyle: React.CSSProperties = {
      backgroundColor: 'var(--color-white)',
    };

    const trStyle: React.CSSProperties = {
      borderBottom: '1px solid var(--color-light-grey)',
      transition: 'background-color 0.2s ease-in-out',
    };

    const tdStyle: React.CSSProperties = {
      padding: '12px',
      fontSize: '14px',
      color: 'var(--color-dark-grey)',
    };

    const paginationStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 20px',
      borderTop: '1px solid var(--color-light-grey)',
      backgroundColor: 'var(--color-light-grey)',
      fontSize: '12px',
      color: 'var(--color-dark-grey)',
      fontFamily: 'var(--font-family)',
    };

    const paginationButtonsStyle: React.CSSProperties = {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    };

    const navButtonStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 10px',
      backgroundColor: 'var(--color-white)',
      border: '1px solid var(--color-border)',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      fontSize: '12px',
      fontFamily: 'var(--font-family)',
    };

    // Menu is now rendered at the document body level using position: fixed
    // and anchored via menuPos, so it is not clipped by the table container.
    const menuStyle: React.CSSProperties = {
      position: 'fixed',
      top: menuPos?.top ?? 0,
      left: menuPos?.left ?? 0,
      backgroundColor: 'var(--color-white)',
      border: '1px solid var(--color-light-grey)',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      zIndex: 1100,
      minWidth: '160px',
    };

    const menuItemStyle: React.CSSProperties = {
      padding: '8px 12px',
      fontSize: '12px',
      fontFamily: 'var(--font-family)',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease-in-out',
      borderBottom: '1px solid var(--color-light-grey)',
    };

    const lastMenuItemStyle: React.CSSProperties = {
      borderBottom: 'none',
    };

    const handleSort = (key: string, isSortable?: boolean) => {
      if (!isSortable) return;
      const newSortOrder =
        sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
      onSort?.(key, newSortOrder);
    };

    // WI #337: an action with `hidden` returning true is dropped for that row.
    const visibleActionsFor = (row: unknown): RowAction<unknown>[] =>
      ((actions ?? []) as RowAction<unknown>[]).filter(
        (action) => !action.hidden?.(row)
      );

    const totalPages = Math.ceil(totalItems / pageSize);
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalItems);

    const handlePrevPage = () => {
      if (currentPage > 1 && onPageChange) {
        onPageChange(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages && onPageChange) {
        onPageChange(currentPage + 1);
      }
    };

    if (isLoading && data.length === 0) {
      return (
        <div style={containerStyle} ref={ref}>
          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead style={theadStyle}>
                <tr>
                  {columns.map((col) => (
                    <th key={String(col.key)} style={thStyle}>
                      {col.header}
                    </th>
                  ))}
                  {actions && <th style={thStyle}>Actions</th>}
                </tr>
              </thead>
              <tbody style={tbodyStyle}>
                {[...Array(5)].map((_, idx) => (
                  <tr key={idx} style={trStyle}>
                    {columns.map((col) => (
                      <td key={String(col.key)} style={tdStyle}>
                        <div
                          style={{
                            height: '20px',
                            backgroundColor: 'var(--color-light-grey)',
                            borderRadius: '4px',
                            animation: 'pulse 1.5s ease-in-out infinite',
                          }}
                        />
                      </td>
                    ))}
                    {actions && (
                      <td style={tdStyle}>
                        <div
                          style={{
                            height: '20px',
                            width: '40px',
                            backgroundColor: 'var(--color-light-grey)',
                            borderRadius: '4px',
                            animation: 'pulse 1.5s ease-in-out infinite',
                          }}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <style>{`@keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }`}</style>
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div style={containerStyle} ref={ref}>
          <EmptyState
            icon={() => <div />}
            heading="No data"
            message={emptyMessage}
          />
        </div>
      );
    }

    return (
      <>
        <style>{`
          @keyframes pulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
        `}</style>
        <div style={containerStyle} ref={ref}>
          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead style={theadStyle}>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={String(col.key)}
                      style={{
                        ...(col.sortable ? sortableTh : thStyle),
                        width: col.width,
                      }}
                      onClick={() => handleSort(String(col.key), col.sortable)}
                      onMouseEnter={(e) => {
                        if (col.sortable) {
                          (e.currentTarget as HTMLElement).style.backgroundColor =
                            'var(--color-magenta-tint)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (col.sortable) {
                          (e.currentTarget as HTMLElement).style.backgroundColor =
                            'var(--color-light-grey)';
                        }
                      }}
                    >
                      <span style={col.sortable ? sortableThContent : undefined}>
                        <span>{col.header}</span>
                        {col.sortable && sortBy === String(col.key) && (
                          <span style={{ display: 'inline-flex' }}>
                            {sortOrder === 'asc' ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                  {actions && <th style={thStyle}>Actions</th>}
                </tr>
              </thead>
              <tbody style={tbodyStyle}>
                {data.map((row, idx) => (
                  <tr
                    key={String(row[rowKey]) || idx}
                    style={{
                      ...trStyle,
                      // Bug #166: hint the row is clickable when onRowClick is provided.
                      cursor: onRowClick ? 'pointer' : 'default',
                    }}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'var(--color-magenta-tint)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'var(--color-white)';
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={String(col.key)}
                        style={{
                          ...tdStyle,
                          width: col.width,
                        }}
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : row[col.key]?.toString() || '-'}
                      </td>
                    ))}
                    {actions && (
                      <td
                        style={tdStyle}
                        // Bug #166: stop propagation on the entire actions cell so
                        // clicking the "⋮" trigger (or any empty space around it)
                        // doesn't also navigate the row.
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          ref={(el) => {
                            if (el) triggerRefs.current.set(row[rowKey], el);
                            else triggerRefs.current.delete(row[rowKey]);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--color-dark-grey)',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (openMenuRowId === row[rowKey]) {
                              setOpenMenuRowId(null);
                              setMenuPos(null);
                            } else {
                              openMenuForRow(row[rowKey], e.currentTarget);
                            }
                          }}
                          aria-label="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {openMenuRowId != null && menuPos && actions && (() => {
            const activeRow = data.find((r) => r[rowKey] === openMenuRowId);
            if (!activeRow) return null;
            return (
              <div style={menuStyle} ref={menuRef} role="menu">
                {visibleActionsFor(activeRow).map((action, aIdx, visible) => (
                  <button
                    key={aIdx}
                    role="menuitem"
                    style={{
                      ...menuItemStyle,
                      ...(aIdx === visible.length - 1 ? lastMenuItemStyle : {}),
                      width: '100%',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      color:
                        action.variant === 'danger'
                          ? '#dc3545'
                          : 'var(--color-dark-grey)',
                    }}
                    onClick={() => {
                      action.onClick(activeRow);
                      setOpenMenuRowId(null);
                      setMenuPos(null);
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'var(--color-light-grey)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'var(--color-white)';
                    }}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            );
          })()}
          {totalItems > 0 && (
            <div style={paginationStyle}>
              <span>
                Showing {startRecord} to {endRecord} of {totalItems}
              </span>
              <div style={paginationButtonsStyle}>
                <button
                  style={navButtonStyle}
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  onMouseEnter={(e) => {
                    if (currentPage > 1) {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'var(--color-magenta)';
                      (e.currentTarget as HTMLElement).style.color =
                        'var(--color-white)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      'var(--color-white)';
                    (e.currentTarget as HTMLElement).style.color =
                      'var(--color-dark-grey)';
                  }}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} />
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  style={navButtonStyle}
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  onMouseEnter={(e) => {
                    if (currentPage < totalPages) {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        'var(--color-magenta)';
                      (e.currentTarget as HTMLElement).style.color =
                        'var(--color-white)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      'var(--color-white)';
                    (e.currentTarget as HTMLElement).style.color =
                      'var(--color-dark-grey)';
                  }}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
);

DataTable.displayName = 'DataTable';
